import { cn } from '@/lib/utils';
import type { UserPublic } from '@/lib/api/types';

const SIZES = {
  sm: 'h-5 w-5 text-[10px]',
  md: 'h-6 w-6 text-[11px]',
  lg: 'h-9 w-9 text-sm',
} as const;

/** Initials, so an avatar still identifies someone when the image is missing. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts.at(-1)?.[0] ?? '')).toUpperCase();
}

export function Avatar({
  user,
  size = 'md',
  className,
}: {
  user: UserPublic;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const label = `${user.display_name} (@${user.username})`;

  if (user.avatar_url) {
    return (
      <img
        src={user.avatar_url}
        alt={label}
        title={label}
        // Google avatars are third-party; if one fails we still want initials,
        // so the fallback is rendered underneath rather than instead.
        className={cn(
          'shrink-0 rounded-full bg-surface-sunken object-cover',
          SIZES[size],
          className,
        )}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        'bg-surface-sunken font-medium text-content-muted',
        SIZES[size],
        className,
      )}
    >
      {initials(user.display_name)}
    </span>
  );
}
